import { Component, OnInit, OnDestroy } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { SharedDialogService } from "src/app/shared/services/shared-dialog.service";
import { NavigationService } from "src/app/shared/services/navigation.service";
import {
  AppRouteConstants,
  PathRouteConstants,
} from "src/app/shared/constants/app-route.constants";
import { UtilityService } from "src/app/shared/services/utility.service";
import { ToastrService } from "ngx-toastr";
import {
  SuccessMessages,
  ErrorMessages,
  ActionMessages,
  NumberConstants,
} from "src/app/shared/constants/utility.constants";
import { LessonsApiService } from "../../services/lessons-api.service";
import { LessonCurriculumApiService } from "../../services/lesson-curriculum-api.service";
import { Lesson } from "../../models/lesson";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { ListBase } from "src/app/shared/models/list-base";
import { AppUser } from "src/app/shared/models/app-user";
import { ApiDataService } from "src/app/shared/services/api.service";
import { StudentConstants } from "src/app/feature-modules/student-list/constants/student.constants";

@Component({
    selector: "app-lesson-list",
    templateUrl: "./lesson-list.component.html",
    styleUrls: ["./lesson-list.component.css"],
    standalone: false
})
export class LessonListComponent implements OnInit, OnDestroy {
  ngUnsubscribe = new Subject <void>();
  basicLimit = NumberConstants.LESSONS_LIMIT;
  searchBase: ListBase = { search: "", offset: 0, limit: this.basicLimit };
  filteredLessonCount: number;
  loading = false;
  filteredLessons: Lesson[] = [];
  loggedInUser: AppUser;
  tutorRoleId = StudentConstants.TUTOR_ROLE_ID;
  activeView: 'lesson' | 'curriculum' = 'lesson';
  curricula: any[] = [];
  selectedTab: string = 'lesson';
  allCurricula: any[] = [];
  filteredCurricula: any[] = [];

  constructor(
    private route: ActivatedRoute,
    private apiService: ApiDataService,
    private lessonsApiService: LessonsApiService,
    private lessonCurriculumApiService: LessonCurriculumApiService,
    private sharedDialogservice: SharedDialogService,
    private navigationService: NavigationService,
    private toastrService: ToastrService
  ) {}

  ngOnInit() {
    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(params => {
        this.activeView = params['tab'] === 'curriculum' ? 'curriculum' : 'lesson';

        this.searchBase.search = '';

         if (this.activeView === 'lesson') {
            this.filteredLessons = [];
            this.getLessons();
          } else {
            this.filteredCurricula = this.allCurricula;
          }
        });
    this.getUserInfo();
    this.getLessons();
    this.getCurricula();
  }

  activeViewChange(view: 'lesson' | 'curriculum') {
    this.navigationService.navigateToPageAsync([PathRouteConstants.LESSONS], {
      queryParams: view === 'curriculum' ? { tab: 'curriculum' } : {},
    });
  }

  getLessons() {
    this.loading = true;
    this.lessonsApiService
      .getAllLessons(this.searchBase)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((res) => {
        if (res) {
          this.filteredLessonCount = res[1];
          this.filteredLessons.push(...res[0]);
        }
        this.loading = false;
      });
  }
  getUserInfo() {
    this.apiService.getUserInfo().subscribe((res) => {
      if (res) {
        this.loggedInUser = res.role.id;
      }
    });
  }
  onScroll() {
    this.searchBase.offset = this.searchBase.offset + this.basicLimit;
    this.getLessons();
  }

  searchChange() {
    this.searchBase.offset = 0;

    if (this.activeView === 'lesson') {
      this.filteredLessons = [];
      this.getLessons();
    } else {
      this.filterCurricula();
    }
  }

  goToLessonDetail(id) {
    this.navigationService.navigateToPage([
      PathRouteConstants.LESSONS +
        UtilityService.replaceText(AppRouteConstants.LESSON_DETAILS, [
          { text: ":id", data: id },
        ]),
    ]);
  }

  updateLesson(id) {
    if (id) {
      this.navigationService.navigateToPage([
        PathRouteConstants.LESSONS +
          UtilityService.replaceText(AppRouteConstants.LESSON_UPDATE, [
            { text: ":id", data: id },
          ]),
      ]);
    } else {
      this.navigationService.navigateToPage([
        PathRouteConstants.LESSONS + AppRouteConstants.LESSON_CREATE,
      ]);
    }
  }

  deleteLesson(lesson) {
    this.lessonCurriculumApiService
      .getCurriculumByLessonPlan(lesson.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((curricula) => {

        let message = UtilityService.replaceText(
          ActionMessages.DELETE_CONFIRMATION,
          [{ text: "_name", data: lesson?.lesson_plan_name }]
        );

        if (curricula && curricula.length > 0) {
          const curriculumNames = curricula
            .map(c => c.lesson_plan_curriculum_name)
            .join(",");

          message =
            `This lesson plan is a part of the following yearly plans(s):<br><br>` +
            `${curriculumNames}<br><br>` +
            `Ensure they are not in active use.\n\n<br>` +
            message;
        } else {
          message = "This lesson plan is not a part of any yearly plan<br><br>" + message;
        }

        this.sharedDialogservice
          .ConfirmAction({
            Title: "Delete " + lesson?.lesson_plan_name,
            Message: message,
          })
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe((res) => { 
            if (res) {
              this.lessonsApiService
                .deleteLesson(lesson.id)
                .pipe(takeUntil(this.ngUnsubscribe))
                .subscribe((result) => {
                  if (result) {
                    this.toastrService.success(SuccessMessages.DELETE_LESSON);
                    this.filteredLessons = this.filteredLessons.filter(
                      (l) => l.id !== lesson.id
                    );
                    this.filteredLessonCount = this.filteredLessons.length;
                  } else {
                    this.toastrService.error(ErrorMessages.LESSON_DELETE);
                  }
                });
            }
          });
      });
  }

  getCurricula() {
    this.lessonCurriculumApiService.getAllCurriculum('lesson_plan')
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(res => {
        if (res) {
          this.allCurricula = res;
          this.filteredCurricula = res;
          this.curricula = res;
        }
      });
  }

  filterCurricula() {
    console.log('Filtering curriculum');
    console.log(this.searchBase.search);
    const searchTerm = (this.searchBase.search || '').toLowerCase();

    this.filteredCurricula = this.allCurricula.filter(c =>
      (c.curriculum_name || '').toLowerCase().includes(searchTerm)
    );
  }

  addNewCurriculum() {
    this.navigationService.navigateToPage([
      PathRouteConstants.LESSONS + AppRouteConstants.CURRICULUM_CREATE,
    ]);
  }

  goToCurriculumDetail(id: number) {
    this.navigationService.navigateToPage([
      PathRouteConstants.LESSONS + UtilityService.replaceText(AppRouteConstants.CURRICULUM_DETAILS, [{ text: ':id', data: id }]),
    ]);
  }

  editCurriculum(id: number) {
    this.navigationService.navigateToPage([
      PathRouteConstants.LESSONS + UtilityService.replaceText(AppRouteConstants.CURRICULUM_UPDATE, [{ text: ':id', data: id }]),
    ]);
  }

  deleteCurriculum(curriculum: any) {
  const message = UtilityService.replaceText(
    ActionMessages.DELETE_CONFIRMATION,
    [{ text: "_name", data: curriculum?.curriculum_name }]
  );

  this.sharedDialogservice
    .ConfirmAction({
      Title: "Delete " + curriculum?.curriculum_name,
      Message: message,
    })
    .pipe(takeUntil(this.ngUnsubscribe))
    .subscribe((confirmed) => {
      if (confirmed) {
        this.lessonCurriculumApiService.deleteCurriculum(curriculum.id)
          .pipe(takeUntil(this.ngUnsubscribe))
          .subscribe(res => {
            if (res) {
              const deletedId = Number(curriculum.id);

              this.curricula = this.curricula.filter(c => Number(c.id) !== deletedId);
              this.allCurricula = this.allCurricula.filter(c => Number(c.id) !== deletedId);
              this.filteredCurricula = this.filteredCurricula.filter(c => Number(c.id) !== deletedId);

              this.toastrService.success('Yearly Plan Deleted');
            } else {
              this.toastrService.error('Failed to Delete Yearly Plan');
            }
          });
      }
    });
}

  ngOnDestroy() {
    this.ngUnsubscribe.next(null);
    this.ngUnsubscribe.complete();
  }
}
